import { useMemo, useState } from 'react';
import { useConnection, useReadContract } from 'wagmi';
import { stakingManagerAbi } from '@syntra/abi';
import { Link } from '../app/router.tsx';
import { CountUp } from '../components/ui/motion.tsx';
import { Icon } from '../components/ui/Icon.tsx';
import { Card, Countdown, Notice, Progress, Rows, SectionHead, StatTile } from '../components/ui/primitives.tsx';
import { CompareBars } from '../components/ui/charts.tsx';
import { AreaChart, Donut, Orb, RingGauge, Sparkline, type OrbData, type XY } from '../components/ui/viz.tsx';
import { DEPLOYMENT as D } from '../config/env.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { useChainNow } from '../hooks/useChainClock.tsx';
import { useIndexer } from '../hooks/useIndexer.ts';
import { usePosition, useStakes } from '../hooks/usePosition.ts';
import { isTimed, PERMANENT, PLAN_IDS, useProtocol, type Protocol } from '../hooks/useProtocol.ts';
import { useRank, type RankStep } from '../hooks/useRank.ts';
import { apyFromRate, E18, fmtBps, fmtNum, fmtPct, fmtSig, fmtToken, fmtUsd, toNum } from '../lib/format.ts';
import { RANK_KEYS } from './Referral.tsx';
import { Coin } from '../components/ui/coins.tsx';
import { RankEmblem } from '../components/ui/RankEmblem.tsx';

interface ActivityRow {
  id: string;
  kind: string;
  amount: string | null;
  timestamp: string;
}

interface EpochRow {
  id: string;
  minted: string;
  stakedValue: string;
  timestamp: string;
}

/** The epoch's yield across all plans (what 1 staked SYN earned on average). */
const epochYield = (e: EpochRow) => (Number(e.stakedValue) > 0 ? Number(e.minted) / Number(e.stakedValue) : 0);
/** Today's per-plan values and rates (SYN, per epoch), for projections at today's rates. */
const planValues = (p: Protocol) => (p.plans ?? []).map((pl) => toNum(pl.totalValue));
const planRates = (p: Protocol) => (p.plans ?? []).map((pl) => toNum(pl.rate));
/** Average per-epoch rate weighted by what is staked in each plan (the no-lock rate when nothing is staked). */
export function blendedRate(values: number[], rates: number[]): number {
  const total = values.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return rates[0] ?? NaN;
  return values.reduce((s, v, i) => s + v * rates[i], 0) / total;
}

const RANGES = [
  { key: '7D', epochs: 14 },
  { key: '14D', epochs: 28 },
  { key: '30D', epochs: 60 },
  { key: '60D', epochs: 120 },
  { key: '90D', epochs: 180 },
] as const;
const PERIODS = [
  { days: 7, key: '7D' },
  { days: 30, key: '30D' },
  { days: 90, key: '90D' },
  { days: 365, key: '1Y' },
] as const;
const EXAMPLE = 1000n * E18;
const dateFmt = (ts: number) => new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** Growth of 1 sSYN through the given epoch rates: 1, (1+r1), (1+r1)(1+r2), … */
export function growthCurve(rates: number[]): number[] {
  const out = [1];
  for (const r of rates) out.push(out[out.length - 1] * (1 + r));
  return out;
}

/** SYN minted per epoch over the next `n` epochs at today's rates: every plan compounds on itself. */
export function rewardsAhead(values: number[], rates: number[], n: number): number[] {
  return Array.from({ length: n }, (_, i) => values.reduce((s, v, p) => s + v * Math.pow(1 + rates[p], i) * rates[p], 0));
}

/** Earnings after `days` at today's per-epoch rates, every plan compounding each epoch. */
export function projectRewards(values: number[], rates: number[], days: number): number {
  const epochs = days * 2;
  return values.reduce((s, v, p) => s + v * (Math.pow(1 + rates[p], epochs) - 1), 0);
}

function InfoTitle({ children, info }: { children: string; info: string }) {
  return (
    <h3>
      {children}
      <span title={info} aria-label={info} role="img" style={{ display: 'inline-flex' }}>
        <Icon name="info" />
      </span>
    </h3>
  );
}

interface LotRow {
  origin: number;
  shares: bigint;
  principalUsd: bigint;
  unlockAt: bigint;
}


// the design's rebase-growth line carries about twenty vertices
const GROWTH_N = 20;
const EPOCH = 43_200; // seconds in a rebase epoch
const ORB_SIZE = 180; // a shorter top row, so more of the dashboard shows at once
function BalanceCard() {
  const { t } = useI18n();
  const { data: p, isLoading } = useProtocol();
  const { address, isConnected } = useConnection();
  const { data: me } = usePosition(address);
  const now = useChainNow();
  const lots = useReadContract({
    address: D.staking,
    abi: stakingManagerAbi,
    functionName: 'lots',
    args: [address ?? '0x0000000000000000000000000000000000000000'],
    query: { enabled: Boolean(address), refetchInterval: 30_000 },
  });
  const epochs = useIndexer<EpochRow[]>(`/epochs?limit=${GROWTH_N}`);
  const amount = isConnected ? me.staked : p.totalStaked;
  const usd = amount !== undefined && p.price ? (amount * p.price) / E18 : undefined;
  const rates = (epochs.data ?? []).map(epochYield).reverse();
  // a line needs two points: until two epochs have been paid, show today's rates applied ahead instead
  const live = rates.length >= 2;
  const rows = (epochs.data ?? []).slice().reverse();
  // SYN minted to all stakers each epoch: real history, or today's rates applied to today's stake ahead
  const minted = live ? rows.map((e) => Number(e.minted) / 1e18) : rewardsAhead(planValues(p), planRates(p), GROWTH_N);
  const titles = live
    ? rows.map((e, i) => `${t('side.epoch')} ${e.id}: ${fmtNum(minted[i], 3)} SYN`)
    : minted.map((v, i) => `${t('dash2.epochN', { n: i + 1 })}: ${fmtNum(v, 3)} SYN`);
  const last = live ? rates[rates.length - 1] : blendedRate(planValues(p), planRates(p));
  // timed locks only: no-lock deposits are free and the permanent plan never unlocks
  const active = ((lots.data as readonly LotRow[] | undefined) ?? []).filter((l) => l.shares > 0n && isTimed(l.origin));
  const pending = active.map((l) => Number(l.unlockAt)).filter((u) => u > now);
  const nextUnlock = pending.length ? Math.min(...pending) : undefined;
  // the orb shows the figures beside it: the balance, the time through this epoch, the share staked, the reward pace
  const orb: OrbData = {
    staked: amount !== undefined ? toNum(amount) : undefined,
    rebaseProgress: p.nextEpochAt !== undefined ? 1 - Math.max(0, Math.min(EPOCH, p.nextEpochAt - now)) / EPOCH : undefined,
    epoch: p.epoch,
    stakedShare: p.stakedShare,
    rate: last,
    maxRate: p.maxRate !== undefined ? toNum(p.maxRate) : undefined,
  };
  return (
    <Card className="balance-card">
      <div className="card-head">
        <h3>{t('dash2.balance')}</h3>
        <span className="live-pill">
          <span className="dot-ok" />
          {t('dash2.rebasing')}
        </span>
      </div>
      <div className="balance">
        <Orb
          size={ORB_SIZE}
          data={orb}
          label={t('dash2.orbLabel', {
            staked: fmtNum(orb.staked ?? NaN, 2),
            pct: fmtPct(orb.rebaseProgress ?? NaN, 0),
            share: fmtPct(orb.stakedShare ?? NaN, 1),
            rate: fmtPct(orb.rate ?? NaN, 3),
          })}
        />
        <div className="bal-main">
          <div className="soft">{isConnected ? t('dash2.staked') : t('dash2.allStaked')}</div>
          <div className="big" data-testid="staked-balance">
            {isLoading && amount === undefined ? <span className="skeleton" /> : <CountUp value={toNum(amount)} format={(v) => fmtNum(v, 2)} />}
          </div>
          <div className="row-unit">
            <span className="unit gradient-text">sSYN</span>
            <span className="soft" style={{ fontSize: 16 }}>
              ≈ <CountUp value={toNum(usd)} format={(v) => fmtUsd(v)} />
            </span>
          </div>
        </div>
        <div className="bal-stats">
          <div className="bal-stat" data-lean>
            <span className="k">{t('dash2.nextRebase')}</span>
            <span className="v">
              <Countdown to={p.nextEpochAt} />
            </span>
          </div>
          <div className="bal-stat" data-lean>
            <span className="k">{t('dash2.index')}</span>
            <span className="v num">{p.plans ? `${toNum(p.plans[0].index).toFixed(4)}×` : '—'}</span>
          </div>
          {isConnected ? (
            <div className="bal-stat" data-lean>
              <span className="k">{t('dash2.nextUnlock')}</span>
              <span className={`v${nextUnlock === undefined ? ' text' : ''}`}>
                {nextUnlock !== undefined ? <Countdown to={nextUnlock} /> : active.length ? t('dash2.allUnlocked') : t('dash2.noLocks')}
              </span>
            </div>
          ) : (
            <div className="bal-stat" data-lean>
              <span className="k">{t('dash2.stakedSupply')}</span>
              <span className="v num">{fmtPct(p.stakedShare ?? NaN, 1)}</span>
            </div>
          )}
        </div>
      </div>
      {/* a size container, so a narrow card gives the line its own full-width row */}
      <div className="growth-wrap">
        <div className="growth-row">
          <span className="lbl">
            <span className="soft">{t('dash2.growth')}</span>
            <small>{t(live ? 'dash2.mintedLastN' : 'dash2.mintedNextN', { n: live ? rates.length : GROWTH_N })}</small>
          </span>
          <Sparkline values={minted} titles={titles} variant="points" height={60} label={t('dash2.growth')} />
          <span className="pct num">{t(live ? 'dash2.thisEpoch' : 'dash2.nextEpoch', { pct: fmtPct(last, 2) })}</span>
        </div>
      </div>
    </Card>
  );
}

/**
 * The rank step in one line, over the progress bar: the current rank's emblem → the next rank's emblem with its binary
 * rate, and how far along. What is still needed (team volume, weaker leg) is in the line's tooltip.
 */
function RankLine({ rank }: { rank: RankStep }) {
  const { t } = useI18n();
  const next = rank.next;
  if (!next) {
    return (
      <div className="rank-line" data-testid="rank-path">
        <span className="rank-hop">
          <RankEmblem rank={rank.rank} size={24} />
          <b>{t('dash2.topRank', { rank: t(RANK_KEYS[rank.rank]), rate: fmtBps(rank.rateBps) })}</b>
        </span>
      </div>
    );
  }
  const needTotal = next.thresholdUsd > rank.totalUsd ? next.thresholdUsd - rank.totalUsd : 0n;
  const needWeaker = next.weakerUsd > rank.weakerLegUsd ? next.weakerUsd - rank.weakerLegUsd : 0n;
  const needs = [
    needTotal > 0n ? t('dash2.needTotal', { amount: fmtUsd(needTotal) }) : null,
    needWeaker > 0n ? t('dash2.needWeaker', { amount: fmtUsd(needWeaker) }) : null,
  ].filter(Boolean);
  return (
    <div className="rank-line" data-testid="rank-path" title={needs.length ? t('dash2.stillNeeded', { needs: needs.join(' · ') }) : t('dash2.reqMet')}>
      <span className="rank-hop now">
        <RankEmblem rank={rank.rank} size={24} />
        <b>{t(RANK_KEYS[rank.rank])}</b>
      </span>
      <span className="rank-arrow" aria-hidden="true" />
      <span className="rank-hop next">
        <RankEmblem rank={next.rank} size={24} />
        <b>{t(RANK_KEYS[next.rank])}</b>
        <span className="soft">· {t('dash2.binaryRate', { rate: fmtBps(next.rateBps) })}</span>
      </span>
      <b className="num rank-pct">{fmtPct(rank.progress, 0)}</b>
    </div>
  );
}

function ApyCard() {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  const { address, isConnected } = useConnection();
  const rank = useRank(address);
  const maxRate = useReadContract({ address: D.staking, abi: stakingManagerAbi, functionName: 'maxRate' });
  const top = p.plans?.[PERMANENT];
  const apy = apyFromRate(top?.paidRate); // what the plan pays today: its rate, or less once the mint budget binds
  const max = maxRate.data as bigint | undefined;
  const fraction = top && max ? Number(top.paidRate) / Number(max) : 0;
  const budget = p.budget;
  return (
    <Card className="apy-card">
      <div className="card-head">
        <InfoTitle info={t('dash2.apyInfo')}>{t('dash2.apy')}</InfoTitle>
        <span className="soft" style={{ fontSize: 13 }}>
          {t('dash2.noLockApy', { apy: fmtPct(apyFromRate(p.plans?.[0].paidRate), 1) })}
        </span>
      </div>
      <RingGauge fraction={fraction} label={t('dash2.currentApy')}>
        <span className="v num">
          <CountUp value={Number.isFinite(apy) ? apy * 100 : undefined} format={(v) => `${Math.round(v).toLocaleString('en-US')}%`} />
        </span>
        <span className="l">{t('dash2.currentApy')}</span>
      </RingGauge>
      <p className="muted" style={{ textAlign: 'center', margin: '8px 0 14px', fontSize: 12 }}>
        {top && max !== undefined
          ? t('dash2.rateOfMax', { rate: fmtPct(Number(top.paidRate) / 1e18, 3), max: fmtPct(Number(max) / 1e18, 3) })
          : ' '}
      </p>
      {budget ? (
        <div className="budget" data-testid="budget">
          <div className="row">
            <span className="k">{t('dash2.budget')}</span>
            <span className="num">{t('dash2.budgetUsed', { used: fmtSig(budget.wanted), cap: fmtSig(budget.cap) })}</span>
          </div>
          <Progress value={budget.cap > 0n ? Math.min(1, toNum(budget.wanted) / toNum(budget.cap)) : 1} />
          <span className={budget.capped ? 'budget-short' : 'muted'}>{budget.capped ? t('dash2.budgetCapped', { pct: fmtPct(budget.factor, 0) }) : t('dash2.rateNote')}</span>
        </div>
      ) : null}
      {!isConnected ? (
        <p className="soft" style={{ margin: 0, fontSize: 13 }}>
          {t('dash2.rankConnect')}
        </p>
      ) : rank ? (
        <div data-testid="rank-progress">
          <RankLine rank={rank} />
          <div style={{ marginTop: 8 }}>
            <Progress value={rank.progress} />
          </div>
        </div>
      ) : (
        <span className="skeleton" />
      )}
    </Card>
  );
}

function OverviewCard() {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  const [range, setRange] = useState<(typeof RANGES)[number]['key']>('30D');
  const want = RANGES.find((r) => r.key === range)!.epochs;
  const epochs = useIndexer<EpochRow[]>('/epochs?limit=180');
  const rows = useMemo(() => (epochs.data ?? []).slice(0, want).reverse(), [epochs.data, want]);
  const live = rows.length >= 2;
  const today = blendedRate(planValues(p), planRates(p));
  const rates = live ? rows.map(epochYield) : Array.from({ length: want }, () => today);
  const curve = growthCurve(rates);
  const points: XY[] = live
    ? curve.map((y, i) => ({ x: i === 0 ? Number(rows[0].timestamp) - 43_200 : Number(rows[i - 1].timestamp), y }))
    : curve.map((y, i) => ({ x: i, y }));
  const avg = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : NaN;
  const end = curve[curve.length - 1];
  return (
    <Card>
      <div className="card-head">
        <InfoTitle info={t('dash2.overviewInfo')}>{t('dash2.overview')}</InfoTitle>
        <div className="range-tabs" role="group" aria-label={t('dash2.overview')}>
          {RANGES.map((r) => (
            <button key={r.key} type="button" aria-pressed={r.key === range} onClick={() => setRange(r.key)}>
              {r.key}
            </button>
          ))}
        </div>
      </div>
      <div className="fill">
        {Number.isFinite(end) ? (
          <AreaChart
            points={points}
            label={t('dash2.overview')}
            formatX={(x) => (live ? dateFmt(x) : t('dash2.epochN', { n: x }))}
            formatY={(y) => `${y.toFixed(y < 1.1 ? 3 : 2)}×`}
            dashed={!live}
            badge={`${end.toFixed(3)}×`}
          />
        ) : (
          <span className="skeleton" />
        )}
        {!live ? <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>{t('dash2.projectedNote')}</p> : null}
      </div>
      <div className="stat-pair">
        <div>
          <div className="k">{t('dash2.rebases')}</div>
          <div className="v num">{live ? rows.length : 0}</div>
          <div className="s">{t('dash2.inPeriod')}</div>
        </div>
        <div>
          <div className="k">{t('dash2.avgRebase')}</div>
          <div className="v num gradient-text">+{fmtPct(avg, 2)}</div>
          <div className="s">{t('dash2.perEpoch')}</div>
        </div>
      </div>
    </Card>
  );
}

function ProjectionCard() {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  const { address, isConnected } = useConnection();
  const { data: me } = usePosition(address);
  const [days, setDays] = useState<(typeof PERIODS)[number]['days']>(30);
  const { lots } = useStakes(address);
  const rates = planRates(p);
  const mine = PLAN_IDS.map((id) => lots.filter((l) => !l.empty && l.plan === id).reduce((s, l) => s + toNum(l.value), 0));
  const hasStake = isConnected && mine.some((v) => v > 0);
  // the example: 1,000 SYN in the no-lock plan (the SYN ↔ sSYN conversion)
  const values = hasStake ? mine : [toNum(EXAMPLE), 0, 0, 0, 0];
  const total = values.reduce((a, b) => a + b, 0);
  const gain = projectRewards(values, rates, days);
  const step = Math.max(1, Math.round(days / 30));
  const points: XY[] = [];
  for (let d = 0; d <= days; d += step) points.push({ x: d, y: projectRewards(values, rates, d) });
  if (points[points.length - 1].x !== days) points.push({ x: days, y: gain });
  const price = toNum(p.price);
  const perDay = projectRewards(values, rates, 1);
  const periodYield = total > 0 ? gain / total : NaN;
  return (
    <Card>
      <div className="card-head">
        <InfoTitle info={t('dash2.projectionInfo')}>{t('dash2.projection')}</InfoTitle>
        <div className="range-tabs" role="group" aria-label={t('dash2.period')}>
          {PERIODS.map((d) => (
            <button key={d.key} type="button" aria-pressed={d.days === days} onClick={() => setDays(d.days)} title={t('dash2.days', { n: d.days })}>
              {d.key}
            </button>
          ))}
        </div>
      </div>
      <div className="big figure-coin" style={{ fontSize: 36 }} data-testid="projection">
        <Coin token="sSYN" size={36} />
        <span>
          +<CountUp value={Number.isFinite(gain) ? gain : undefined} format={(v) => fmtNum(v, 2)} /> <span className="gradient-text unit" style={{ fontSize: 22 }}>sSYN</span>
        </span>
      </div>
      <div className="soft" style={{ marginBottom: 6 }}>
        ≈ <CountUp value={Number.isFinite(gain * price) ? gain * price : undefined} format={(v) => fmtUsd(v)} />
        {hasStake ? null : <span className="muted"> · {t('dash2.example')}</span>}
      </div>
      <div className="fill">
        {Number.isFinite(gain) ? (
          <AreaChart
            key={days}
            points={points}
            label={t('dash2.projection')}
            formatX={(x) => t('dash2.day', { n: x })}
            formatY={(y) => fmtNum(y, 2)}
            arrow
          />
        ) : (
          <span className="skeleton" />
        )}
      </div>
      <div className="stat-pair">
        <div>
          <div className="k">{t('dash2.perDay')}</div>
          <div className="v num">+{Number.isFinite(perDay) ? fmtNum(perDay, 3) : '—'}</div>
          <div className="s">{t('dash2.atRate')}</div>
        </div>
        <div>
          <div className="k">{t('dash2.periodYield')}</div>
          <div className="v num gradient-text">+{fmtPct(periodYield, 2)}</div>
          <div className="s">{t('dash2.onStake')}</div>
        </div>
      </div>
    </Card>
  );
}

function AllocationCard() {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  const { address, isConnected } = useConnection();
  const { data: me } = usePosition(address);
  let slices;
  let share: number;
  const { lots } = useStakes(address);
  const stakedAll = p.totalStaked ?? 0n;
  const lockedAll = (p.plans ?? []).slice(1).reduce((s, pl) => s + pl.totalValue, 0n);
  const noLockAll = p.plans?.[0].totalValue ?? 0n;
  const live = lots.filter((l) => !l.empty);
  const myNoLock = live.filter((l) => l.plan === 0).reduce((s, l) => s + l.value, 0n);
  const myLocked = live.filter((l) => l.plan !== 0).reduce((s, l) => s + l.value, 0n);
  if (isConnected) {
    const avail = me.syntra ?? 0n;
    // rewards: what has compounded above the deposits (the permanent plan's withdrawable part included)
    const deposits = live.reduce((s, l) => s + l.principal, 0n);
    const staked = myNoLock + myLocked;
    const rewards = staked > deposits ? staked - deposits : 0n;
    const total = staked + avail + rewards;
    share = total > 0n ? Number((staked * 10_000n) / total) / 10_000 : 0;
    slices = [
      { key: 'staked', label: t('dash2.stakedShort'), value: toNum(staked - rewards), display: `${fmtToken(staked - rewards, 2)} SYN`, color: 'var(--chart-1)' },
      { key: 'available', label: t('dash2.available'), value: toNum(avail), display: `${fmtToken(avail, 2)} SYN`, color: 'var(--chart-2)' },
      { key: 'rewards', label: t('dash2.rewards'), value: toNum(rewards), display: `${fmtToken(rewards, 4)} SYN`, color: 'var(--chart-3)' },
    ];
  } else {
    const rest = p.supply !== undefined && p.supply > stakedAll ? p.supply - stakedAll : 0n;
    share = p.stakedShare ?? 0;
    slices = [
      { key: 'locked', label: t('dash2.locked'), value: toNum(lockedAll), display: `${fmtToken(lockedAll, 2)} sSYN`, color: 'var(--chart-1)' },
      { key: 'flex', label: t('dash2.noLock'), value: toNum(noLockAll), display: `${fmtToken(noLockAll, 2)} sSYN`, color: 'var(--chart-2)' },
      { key: 'rest', label: t('dash2.unstaked'), value: toNum(rest), display: `${fmtToken(rest, 2)} SYN`, color: 'var(--chart-3)' },
    ];
  }
  return (
    <Card>
      <div className="card-head">
        <InfoTitle info={t(isConnected ? 'dash2.allocationInfo' : 'dash2.allocationAllInfo')}>{t('dash2.allocation')}</InfoTitle>
      </div>
      <div className="fill">
        <Donut
          slices={slices}
          label={t('dash2.allocation')}
          center={
            <>
              <b className="num">
                <CountUp value={share * 100} format={(v) => `${Math.round(v)}%`} />
              </b>
              <span>{t(isConnected ? 'dash2.stakedShort' : 'dash2.ofSupply')}</span>
            </>
          }
        />
      </div>
      {isConnected ? (
        <div className="stat-pair">
          <div>
            <div className="k">{t('dash2.locked')}</div>
            <div className="v num">{fmtToken(myLocked, 2)}</div>
            <div className="s">{t('dash2.lockedPlans')}</div>
          </div>
          <div>
            <div className="k">{t('dash2.noLock')}</div>
            <div className="v num">{fmtToken(myNoLock, 2)}</div>
            <div className="s">{t('dash2.anytime')}</div>
          </div>
        </div>
      ) : (
        <div className="stat-pair">
          <div>
            <div className="k">{t('dash2.totalStaked')}</div>
            <div className="v num">{fmtToken(stakedAll, 2)}</div>
            <div className="s">{t('dash2.lockedPlusFlex')}</div>
          </div>
          <div>
            <div className="k">{t('dash2.stakedValue')}</div>
            <div className="v num">{fmtUsd(p.price ? (stakedAll * p.price) / E18 : undefined)}</div>
            <div className="s">{t('dash2.atTwap')}</div>
          </div>
        </div>
      )}
      <Link to="/stake" className="btn btn-primary btn-block cta">
        {t('dash2.stakeMore')}
        <Icon name="arrow" />
      </Link>
    </Card>
  );
}

export function Dashboard() {
  const { t } = useI18n();
  const { data: p, isLoading } = useProtocol();
  const { address, isConnected } = useConnection();
  const { data: me } = usePosition(address);
  const activity = useIndexer<ActivityRow[]>(address ? `/activity/${address}?limit=8` : null, 15_000);
  const usd = (tokens: bigint | undefined) => (tokens !== undefined && p.price ? (tokens * p.price) / E18 : undefined);
  const premium = p.marketCap !== undefined && p.treasuryUsd ? toNum(p.marketCap) / toNum(p.treasuryUsd) : NaN;

  return (
    <div className="dash">
      <div className="dash-hero">
        <BalanceCard />
        <ApyCard />
      </div>
      <div className="dash-row">
        <OverviewCard />
        <ProjectionCard />
        <AllocationCard />
      </div>

      <SectionHead eyebrow={t('dash.eyebrow')} title={t('dash.title')} />
      <div className="grid tiles">
        <StatTile label={t('dash.price')} value={fmtUsd(p.price)} sub={t('dash.priceSub', { spot: fmtUsd(p.spot) })} loading={isLoading} coin="SYN" />
        <StatTile label={t('dash.marketCap')} value={fmtUsd(p.marketCap)} loading={isLoading} />
        <StatTile label={t('dash.treasury')} value={fmtUsd(p.treasuryUsd)} sub={t('dash.treasurySub')} loading={isLoading} />
        <StatTile label={t('dash.backing')} value={fmtUsd(p.backingPerToken)} loading={isLoading} />
        <StatTile label={t('dash.staked')} value={fmtPct(p.stakedShare ?? NaN, 1)} sub={t('dash.stakedSub')} loading={isLoading} />
        <StatTile label={t('dash.apyPermanent')} value={fmtPct(apyFromRate(p.plans?.[PERMANENT].paidRate), 0)} sub={t('dash.apySub', { rate: fmtPct(toNum(p.plans?.[PERMANENT].paidRate), 3) })} loading={isLoading} />
        <StatTile label={t('dash.apyNoLock')} value={fmtPct(apyFromRate(p.plans?.[0].paidRate), 1)} sub={t('dash.apySub', { rate: fmtPct(toNum(p.plans?.[0].paidRate), 4) })} loading={isLoading} />
        <StatTile label={t('dash.index')} value={p.plans ? toNum(p.plans[0].index).toFixed(4) : '—'} loading={isLoading} />
        <StatTile label={t('dash.nextRebase')} value={<Countdown to={p.nextEpochAt} />} loading={isLoading} />
        <StatTile
          label={t('dash.runway')}
          value={p.runwayDays === undefined ? '—' : p.runwayDays === Infinity ? '∞' : t('dash.runwayDays', { n: fmtNum(p.runwayDays, 1) })}
          sub={t('dash.runwaySub')}
          loading={isLoading}
        />
      </div>

      <div className="grid two">
        <Card title={t('dash.mcapVsBacking')}>
          <CompareBars
            rows={[
              { label: t('dash.marketCap'), value: toNum(p.marketCap), display: fmtUsd(p.marketCap), color: 'var(--chart-1)' },
              { label: t('dash.treasury'), value: toNum(p.treasuryUsd), display: fmtUsd(p.treasuryUsd), color: 'var(--chart-2)' },
            ]}
          />
          {Number.isFinite(premium) ? (
            <p className="soft" style={{ marginBottom: 0 }}>
              {t('dash.premium', { x: premium.toFixed(2) })}
            </p>
          ) : null}
        </Card>
        <Card title={t('dash.yourPosition')}>
          {!isConnected ? (
            <p className="soft">{t('common.connectFirst')}</p>
          ) : (
            <Rows
              items={[
                [t('dash.wallet'), `${fmtToken(me.syntra)} SYN · ${fmtUsd(usd(me.syntra))}`],
                [t('dash.stakedAll'), `${fmtToken(me.staked)} sSYN · ${fmtUsd(usd(me.staked))}`],
                [t('dash.bonds'), t('dash.bondCount', { n: me.bondIds?.length ?? 0 })],
                [t('dash.referral'), fmtUsd(me.referralPendingUsd)],
              ]}
            />
          )}
        </Card>
      </div>

      <div className="grid two">
        <Card title={t('dash.how')}>
          <ol className="soft" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>{t('dash.how1')}</li>
            <li>{t('dash.how2')}</li>
            <li>{t('dash.how3')}</li>
            <li>{t('dash.how4')}</li>
          </ol>
        </Card>
        <Card title={t('dash.activity')}>
          {!isConnected ? (
            <p className="soft">{t('common.connectFirst')}</p>
          ) : !activity.configured ? (
            <Notice>{t('dash.activityNeedsIndexer')}</Notice>
          ) : (activity.data ?? []).length === 0 ? (
            <p className="soft">{t('dash.noActivity')}</p>
          ) : (
            <table className="table stack" data-testid="activity">
              <tbody>
                {(activity.data ?? []).map((a) => (
                  <tr key={a.id}>
                    <td>{t(`act.${a.kind}` as MessageKey)}</td>
                    <td className="num">{a.amount ? `${fmtToken(BigInt(a.amount))} SYN` : ''}</td>
                    <td className="muted">{new Date(Number(a.timestamp) * 1000).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        {t('common.notAdvice')}
      </p>
    </div>
  );
}
